import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Logo } from 'assets';
import { useTheme } from 'hooks';
import { imageTypeKeys } from '../../store/root';
import { usePhotoSeriesStore } from '../../store/root.hook';
import { PhotoSeries } from 'redux/photoseries/photoseries';
import { getDamageCaptureProgress, selectSelectedWorkflow } from 'redux/root.selectors';
import { getPhotoSeries } from 'redux/photoseries/photoseries.selectors';
import { ImageTypeItem } from './containers/image-type-item.component';
import { ImageTypeContent } from './containers/image-type-content.component';
import { TRANSLATIONS_VALUES_KEYS } from 'redux/internationalization/internationalization';
import { ImageTypeKeys, IMAGE_TYPES, IWorkflow, VIN_SUB_TYPES } from 'redux/workflows/workflows';
import { getIcon, instructionsStateFactory } from 'components/pages/photoseries/utils/photoseries.util';
import { useTranslate } from 'services';

const imageTypeItemHeight = 3.3125;

const CaptureWrapper = styled.div`
  display: flex;

  & > div {
    width: 50%;
  }
`;

const CaptureWrapperCol1 = styled.div<{ background?: string }>`
  background: ${({ background }) => background};
  padding: 2rem 1.75rem;
  min-height: 100%;
  max-height: 100%;
  overflow: hidden;
  width: 100%;
`;

const LogoWrapper = styled.div<{ background?: string }>`
  background: ${({ background }) => background};
  width: 100%;
  margin-bottom: 1.5rem;

  & .completed-text {
    font-size: 0.7rem;
    font-weight: 700;
    width: 100%;
    text-align: right;
  }
`;

const ImageTypesList = styled.ul`
  overflow-y: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;
  scroll-behavior: smooth;
  position: relative;
  margin: 0;
  // Display a maximum of 5 items in the list
  height: ${5 * imageTypeItemHeight}rem;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CaptureWrapperCol2 = styled.div<{ background?: string }>`
  background: ${({ background }) => background};
  overflow-y: scroll;
  position: relative;
`;

const { vehicle_scan, process_flow } = TRANSLATIONS_VALUES_KEYS;

export type Params = {
  vehicleType: string;
};

function CaptureInfo() {
  const { styles } = useTheme();
  const liRefs = useRef([]);
  const ImageTypeItemsContainerRef = useRef<HTMLUListElement>(null!);
  const { vehicleType } = useParams<Params>();
  const photoSeries: PhotoSeries = useSelector(getPhotoSeries);
  const selectedWorkflow: IWorkflow = useSelector(selectSelectedWorkflow);
  const {
    captureInfo, instructions, dispatchSetCaptureInfoSelectedImageType, dispatchSetCaptureInfoImageTypes,
    dispatchSetCaptureInfoIsAllImagesCaptured
  } = usePhotoSeriesStore();
  const subType = instructions.subType;
  const imageSubType = subType && subType.subTypes[subType.selectedSubTypeIndex];
  const totalImagesToCapture =
    captureInfo.selectedImageType &&
    instructionsStateFactory(captureInfo?.selectedImageType!, Number(vehicleType), photoSeries, imageSubType?.subTypeDigit).totalImagesToBeCapture;
  const vinImageType = photoSeries.imageTypeSettings && photoSeries.imageTypeSettings.find(({ imageType }) => imageType === ImageTypeKeys.Vin);
  const isInsStickerAnEnabledImgSubType = vinImageType?.enabledImageSubTypes?.includes(VIN_SUB_TYPES.INSPECTION_STICKER);
  const { translate, translateToString } = useTranslate();
  const damageCaptureProgress = useSelector(getDamageCaptureProgress);

  useEffect(() => {
    if (selectedWorkflow) {
      selectedWorkflow.imageTypeSettings.filter(((imageTypeSetting) => imageTypeSetting.enabled)).forEach((imageTypeSetting) => {
        const imgType = imageTypeSetting.imageType;
        const imgTypeStr = imageTypeKeys.getStringKey()[imgType];
        const isInsStickerSelected = isInsStickerAnEnabledImgSubType && imgType === ImageTypeKeys.Vin;

        const isCustomImageType = imgTypeStr === 'custom';
        const captureInfo : { [key: string]: string | null | React.ReactNode } = {
          title: '',
          subTitle: '',
          buttonText: '',
          noImagesCheckboxText: null,
          allCapturedCheckboxText: null
        };

        if (isCustomImageType) {
          captureInfo.title = translateToString(`${imageTypeSetting.customImageTypeId}.title_text`);
          captureInfo.subTitle = translateToString(`${imageTypeSetting.customImageTypeId}.instructions_text`);
          captureInfo.noImagesCheckboxText = translate(`${imageTypeSetting.customImageTypeId}.no_images_checkbox_text`);
          captureInfo.allCapturedCheckboxText = translate(`${imageTypeSetting.customImageTypeId}.all_captured_checkbox_text`);
        } else {
          captureInfo.title = isInsStickerSelected ? process_flow.inspection_sticker.heading : process_flow[imgTypeStr].heading;
          captureInfo.subTitle = isInsStickerSelected ? process_flow.inspection_sticker.sub_heading : process_flow[imgTypeStr].sub_heading;
          captureInfo.buttonText = isInsStickerSelected ? process_flow.inspection_sticker.button_text : process_flow[imgTypeStr].button_text;
        }

        dispatchSetCaptureInfoImageTypes(imgTypeStr, imageTypeSetting.customImageTypeId, captureInfo);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSubType?.subTypeDigit, selectedWorkflow, isInsStickerAnEnabledImgSubType]);

  useEffect(() => {
    const enabledImageTypeSettings = selectedWorkflow.imageTypeSettings.filter((imageTypeSetting) => imageTypeSetting.enabled);
    const isImageTypeNotSelected = enabledImageTypeSettings?.length && !captureInfo.selectedImageType;

    if (isImageTypeNotSelected) {
      dispatchSetCaptureInfoSelectedImageType(enabledImageTypeSettings[0].imageType, enabledImageTypeSettings[0].customImageTypeId);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    selectedWorkflow.imageTypes,
    captureInfo.selectedImageType,
    selectedWorkflow.imageTypeSettings,
  ]);

  liRefs.current = selectedWorkflow.imageTypes?.map((_, i) => liRefs.current[i] ?? React.createRef());

  // This is feature where we do not ask additional images from user if photoSeries.additionalCloseUpsOnly and
  // A) User did not mark any damages
  // B) User marked damages and all are captured
  useEffect(() => {
    if (!damageCaptureProgress) return;

    const additionalImageTypeActive = captureInfo.selectedImageType === ImageTypeKeys.Additional;
    const additionalCloseUpsOnly = photoSeries.additionalCloseUpsOnly;
    const allCloseUpsCaptured = damageCaptureProgress.total === damageCaptureProgress?.current;

    if (additionalImageTypeActive && additionalCloseUpsOnly && allCloseUpsCaptured) {
      const additionalImageTypeCaptureInfo = captureInfo.imageTypes.additional;
      if (additionalImageTypeCaptureInfo && !additionalImageTypeCaptureInfo.isCaptured) {
        dispatchSetCaptureInfoImageTypes(imageTypeKeys.getStringKey()[ImageTypeKeys.Additional], null, {
          ...additionalImageTypeCaptureInfo,
          isCaptured: true,
        });

        dispatchSetCaptureInfoIsAllImagesCaptured(true);
      }
    }
  }, [
    photoSeries.additionalCloseUpsOnly,
    damageCaptureProgress,
    captureInfo.selectedImageType,
    captureInfo.imageTypes,
    dispatchSetCaptureInfoImageTypes,
    dispatchSetCaptureInfoIsAllImagesCaptured
  ]);

  const imageTypeNodes = useMemo(() => {
    if (Object.keys(captureInfo.imageTypes).length === 0) return null;

    const imagesTypes = selectedWorkflow.imageTypeSettings?.filter((imageTypeSetting) => imageTypeSetting.enabled).map((imageTypeSetting, i) => {
      const imageType: number = imageTypeSetting.imageType;
      const customImageTypeId: string | null = imageTypeSetting.customImageTypeId;
      const isCustomImageType = customImageTypeId !== null;

      const isCurrValInspSticker = imageType === ImageTypeKeys.Vin && isInsStickerAnEnabledImgSubType;
      const name = isCustomImageType
        ? translate(`${imageTypeSetting.customImageTypeId}.title_text`)
        : translate(isCurrValInspSticker ? vehicle_scan.instr_modal_title_sticker : IMAGE_TYPES[imageType as ImageTypeKeys].name);

      const listItemKey = isCustomImageType
        ? `${imageType}-${customImageTypeId}`
        : imageType;

      const isImageTypeActive = imageType === captureInfo.selectedImageType
        && customImageTypeId === captureInfo.selectedCustomImageTypeId;

      const imageTypeKey = imageTypeKeys.getStringKey()[imageType!];
      const isCompletelyCaptured: boolean = imageTypeKey !== 'custom'
        ? captureInfo.imageTypes[imageTypeKey]?.isCaptured!
        : captureInfo.imageTypes.custom![customImageTypeId!].isCaptured!;

      return (
        <ImageTypeItem
          key={listItemKey}
          ref={liRefs.current[i]}
          sNo={i + 1}
          name={name}
          height={imageTypeItemHeight}
          containerRef={ImageTypeItemsContainerRef}
          isActive={isImageTypeActive}
          icon={getIcon(IMAGE_TYPES[imageType as ImageTypeKeys].icon, { stroke: styles.colorParagraph })}
          isCompletelyCaptured={isCompletelyCaptured}
        />
      );
    });

    return imagesTypes;
  }, [
    isInsStickerAnEnabledImgSubType,
    captureInfo.selectedImageType,
    captureInfo.imageTypes,
    captureInfo.selectedCustomImageTypeId,
    styles.colorParagraph,
    selectedWorkflow.imageTypeSettings,
    translate
  ]);

  if (!captureInfo.selectedImageType) return null;

  return (
    <CaptureWrapper className="wrapper">
      <CaptureWrapperCol1
        background={styles.colorBackgroundSurface}
      >
        <LogoWrapper
          background={styles.colorBackgroundSurface}
        >
          <Logo />
          <div className="completed-text" style={{ color: styles.colorSuccess }}>
            {captureInfo.isAllImagesCaptured && translate(vehicle_scan.completed_text)}
          </div>
        </LogoWrapper>
        {
          imageTypeNodes && (
            <ImageTypesList
              ref={ImageTypeItemsContainerRef}
              // Allow scrolling until 2 elements are visible
              style={{ paddingBottom: imageTypeNodes.length <= 5 ? 0 : `${3 * imageTypeItemHeight}rem` }}
              data-testid="captureImageTypeList"
            >
              {imageTypeNodes}
            </ImageTypesList>
          )
        }
      </CaptureWrapperCol1>

      <CaptureWrapperCol2
        background={styles.colorBackgroundBase}
      >
        <ImageTypeContent
          state={captureInfo}
          imagesToBeCaptured={totalImagesToCapture}
        />
      </CaptureWrapperCol2>
    </CaptureWrapper>
  );
}

export default CaptureInfo;
